import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import MRCard from "./MRCard";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useMRData } from "./MRDataProvider";

export default function MRKanban({ mrData, onMRClick }) {
  const { updateMR } = useMRData();

  // Normalize status for each record and group by status
  const groupedData = mrData.reduce(
    (acc, mr) => {
      // Normalize status: prefer Status, then status, fallback to "Planned"
      let status = (mr.Status || mr.status || "").toString().trim();
      if (status !== "Planned" && status !== "Completed") {
        status = "Planned";
      }
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(mr);
      return acc;
    },
    { Planned: [], Completed: [] }
  );

  // Design colors
  const headerBg = "#c6f6fa";
  const headerText = "#222";
  const headerFontWeight = 700;
  const headerFontSize = 20;
  const badgeBg = "#3ed6e7";
  const badgeColor = "#fff";
  const badgeFontWeight = 600;
  const badgeFontSize = 16;

  // Handle drag end
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    // Find the MR being moved
    const sourceList = groupedData[source.droppableId];
    const mr = sourceList[source.index];
    const newStatus = destination.droppableId;

    // Optimistically update UI (optional, not implemented here)
    // Actually update status in backend
    try {
      await updateMR(mr.ID || mr.id, { status: newStatus });
      // Optionally: show a toast or notification
    } catch (e) {
      // Optionally: show error
      // Optionally: revert UI
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box sx={{ display: "flex", gap: 4, justifyContent: "center" }}>
        {/* Planned Column */}
        <Droppable droppableId="Planned">
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ width: 600 }}
            >
              {/* Header */}
              <Box
                sx={{
                  bgcolor: headerBg,
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  borderTop: "5px solid #3ed6e7",
                  px: 3,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: headerFontWeight,
                    color: headerText,
                    fontSize: headerFontSize,
                  }}
                >
                  Planned
                </Typography>
                <Box
                  sx={{
                    bgcolor: badgeBg,
                    color: badgeColor,
                    fontWeight: badgeFontWeight,
                    fontSize: badgeFontSize,
                    px: 2,
                    py: 0.5,
                    borderRadius: "16px",
                    minWidth: 32,
                    height: 28,
                    boxShadow: "0 1px 4px 0 rgba(0,0,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {groupedData.Planned.length}
                </Box>
              </Box>
              {/* Card List */}
              <Box
                sx={{
                  width: 600,
                  overflowY: "auto",
                  py: 2,
                  px: 0,
                  bgcolor: "#f8fafc",
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                  maxHeight: "calc(100vh - 180px)",
                  minHeight: 100,
                }}
              >
                {groupedData.Planned.map((mr, idx) => (
                  <Draggable
                    key={mr.ID || mr.id || idx}
                    draggableId={String(mr.ID || mr.id || idx)}
                    index={idx}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: 16,
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            mb: 0,
                            mx: 2,
                            borderRadius: 3,
                            boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)",
                            p: 2,
                            cursor: "pointer",
                            transition: "box-shadow 0.2s",
                            "&:hover": {
                              boxShadow: "0 4px 16px 0 rgba(0,0,0,0.10)",
                            },
                          }}
                          onClick={() => onMRClick(mr)}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: "#4F46E5",
                              fontWeight: 600,
                              fontSize: 16,
                              mb: 0.5,
                              //textDecoration: "underline",
                              //cursor: "pointer",
                            }}
                          >
                            {`MR-${mr.ID || mr.id || "New"}`}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#222", mb: 1 }}
                          >
                            {mr.title || "Management Review"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#444", flex: 1, minWidth: 0 }}
                            >
                              <span>
                                <strong>Note Taker: </strong>
                              </span>
                              {mr.Note_taker_for_this_Management_Review ||
                                mr.noteTaker ||
                                "Not Assigned"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "#888", fontSize: 13 }}
                            >
                              <span>
                                <strong>Management Review Date: </strong>
                              </span>
                              {mr.Date_of_this_Management_Review ||
                                (mr.date
                                  ? new Date(mr.date).toLocaleDateString(
                                      "en-GB",
                                      {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      }
                                    )
                                  : "No Date")}
                            </Typography>
                          </Box>
                        </Paper>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            </Box>
          )}
        </Droppable>
        {/* Completed Column */}
        <Droppable droppableId="Completed">
          {(provided, snapshot) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              sx={{ width: 600 }}
            >
              {/* Header */}
              <Box
                sx={{
                  bgcolor: "lightgreen",
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  borderTop: "5px solid darkgreen",
                  px: 3,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: headerFontWeight,
                    color: headerText,
                    fontSize: headerFontSize,
                  }}
                >
                  Completed
                </Typography>
                <Box
                  sx={{
                    bgcolor: "lightgray",
                    border: "1px solidrgb(3, 210, 234)",
                    color: "white",
                    fontWeight: badgeFontWeight,
                    fontSize: badgeFontSize,
                    px: 2,
                    py: 0.5,
                    borderRadius: "16px",
                    minWidth: 32,
                    height: 28,
                    boxShadow: "0 1px 4px 0 rgba(0,0,0,0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {groupedData.Completed.length}
                </Box>
              </Box>
              {/* Card List */}
              <Box
                sx={{
                  width: 600,
                  overflowY: "auto",
                  py: 2,
                  px: 0,
                  bgcolor: "#f8fafc",
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.03)",
                  maxHeight: "calc(100vh - 180px)",
                  minHeight: 100,
                }}
              >
                {groupedData.Completed.map((mr, idx) => (
                  <Draggable
                    key={mr.ID || mr.id || idx}
                    draggableId={String(mr.ID || mr.id || idx)}
                    index={idx}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          marginBottom: 16,
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            mb: 0,
                            mx: 2,
                            borderRadius: 3,
                            boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)",
                            p: 2,
                            cursor: "pointer",
                            transition: "box-shadow 0.2s",
                            "&:hover": {
                              boxShadow: "0 4px 16px 0 rgba(0,0,0,0.10)",
                            },
                          }}
                          onClick={() => onMRClick(mr)}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: "#4F46E5",
                              fontWeight: 600,
                              fontSize: 16,
                              mb: 0.5,
                              //textDecoration: "underline",
                              //cursor: "pointer",
                            }}
                          >
                            {`MR-${mr.ID || mr.id || "New"}`}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "#222", mb: 1 }}
                          >
                            {mr.title || "Management Review"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: "#444", flex: 1, minWidth: 0 }}
                            >
                              <span>
                                <strong>Note Taker: </strong>
                              </span>
                              {mr.Note_taker_for_this_Management_Review ||
                                mr.noteTaker ||
                                "Not Assigned"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "#888", fontSize: 13 }}
                            >
                              <span>
                                <strong>Management Review Date: </strong>
                              </span>
                              {mr.Date_of_this_Management_Review ||
                                (mr.date
                                  ? new Date(mr.date).toLocaleDateString(
                                      "en-GB",
                                      {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      }
                                    )
                                  : "No Date")}
                            </Typography>
                          </Box>
                        </Paper>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            </Box>
          )}
        </Droppable>
      </Box>
    </DragDropContext>
  );
}
